<template>
  <infinity-page
    icon="$vuetify.icons.user"
  >
    <template #title>
      <h1
        ref="settings"
        class="infinity-text"
      >
        My profile
      </h1>
      <h2 style="font-weight: 200">
        My infinity settings
      </h2>
    </template>

    <v-form>
      <v-container class="py-0">
        <v-row>
          <v-col
            cols="12"
            md="6"
          >
            <v-text-field
              readonly
              label="First Name"
              :value="userInfo.given_name"
            />
          </v-col>

          <v-col
            cols="12"
            md="6"
          >
            <v-text-field
              readonly
              label="Last Name"
              :value="userInfo.family_name"
            />
          </v-col>

          <v-col
            cols="12"
          >
            <v-text-field
              readonly
              label="Email Address"
              :value="userInfo.email"
            />
          </v-col>

          <v-col
            cols="12"
          >
            <v-text-field
              color="purple"
              readonly
              label="IDE URL"
              :value="ideUrl"
            >
              <template
                v-if="ideStatus === 'running'"
                v-slot:append
              >
                <v-btn
                  icon
                  :href="ideUrl"
                  target="_blank"
                >
                  <v-icon dark>
                    mdi-open-in-new
                  </v-icon>
                </v-btn>
              </template>
              <template v-slot:append-outer>
                <v-btn
                  fab
                  dark
                  small
                  :loading="ideDeploying"
                  :color="ideButtonColor"
                  @click="ideButtonClick"
                >
                  <v-icon dark>
                    {{ ideButtonIcon }}
                  </v-icon>
                </v-btn>
              </template>
            </v-text-field>
          </v-col>

          <v-col
            cols="12"
          >
            <v-text-field
              color="purple"
              readonly
              label="Test URL"
              :value="testUrl"
            >
              <template                
                v-slot:append
              >
                <v-btn
                  icon
                  :href="testUrl"
                  target="_blank"
                >
                  <v-icon dark>
                    mdi-open-in-new
                  </v-icon>
                </v-btn>
              </template>              
            </v-text-field>
          </v-col>

          <v-col
            cols="12"
          >
            <v-text-field
              color="purple"
              readonly
              label="Notebook URL"
              :value="notebookUrl"
            >
              <template
                v-if="notebookStatus === 'running'"
                v-slot:append
              >
                <v-btn
                  icon
                  :href="notebookUrl"
                  target="_blank"
                >
                  <v-icon dark>
                    mdi-open-in-new
                  </v-icon>
                </v-btn>
              </template>
              <template v-slot:append-outer>
                <v-btn
                  fab
                  dark
                  small
                  :loading="notebookDeploying"
                  :color="notebookButtonColor"
                  @click="notebookButtonClick"
                >
                  <v-icon dark>
                    {{ notebookButtonIcon }}
                  </v-icon>
                </v-btn>
              </template>
            </v-text-field>
          </v-col>
        </v-row>
      </v-container>
    </v-form>

    <template #sidebar>
      <infinity-sidebar
        icon="$vuetify.icons.settings"
        title="settings"
      >
        <v-list>
          <v-list-item>
            <v-list-item-icon>
              <v-icon>
                $vuetify.icons.user
              </v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              settings
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </infinity-sidebar>
    </template>
  </infinity-page>
</template>

<script>
  export default {
    name: 'UserProfileView',
    data: () => {
      return {
        ideInterval: null,
        notebookInterval: null,
      }
    },
    computed: {
      userInfo () {
        return this.parseJwt(this.$cookies.get('itkn'))
      },
      rfcUsername () {
        const username = this.userInfo?.preferred_username || ''
        return username.replaceAll(/[^a-zA-Z\d]/g, '')
      },
      ideUrl () {
        return `https://ide-${this.rfcUsername}.${document.location.host}`
      },
      testUrl () {
        return `https://test-${this.rfcUsername}.${document.location.host}`
      },
      notebookUrl () {
        return `https://notebook-${this.rfcUsername}.${document.location.host}`
      },
      ideStatus: {
        get () {
          return this.$store.state.app.ideStatus
        },
        set (v) {
          this.$store.commit('app/ideStatus', v)
        },
      },
      notebookStatus: {
        get () {
          return this.$store.state.app.notebookStatus
        },
        set (v) {
          this.$store.commit('app/notebookStatus', v)
        },
      },
      ideButtonColor () {
        switch (this.ideStatus) {
          case 'stopped': return 'green'
          case 'running': return 'red'
          case 'deploying': return 'orange'
        }
        return 'red'
      },
      notebookButtonColor () {
        switch (this.notebookStatus) {
          case 'stopped': return 'green'
          case 'running': return 'red'
          case 'deploying': return 'orange'
        }
        return 'red'
      },
      ideButtonIcon () {
        switch (this.ideStatus) {
          case 'stopped': return 'mdi-play'
          case 'running': return 'mdi-stop'
        }
        return ''
      },
      notebookButtonIcon () {
        switch (this.notebookStatus) {
          case 'stopped': return 'mdi-play'
          case 'running': return 'mdi-stop'
        }
        return ''
      },
      ideDeploying () {
        return this.ideStatus === 'deploying'
      },
      notebookDeploying () {
        return this.notebookStatus === 'deploying'
      },
    },
    mounted () {
      this.resetIdeInterval()
      this.resetNotebookInterval()
    },
    unmounted () {
      clearInterval(this.ideInterval)
      clearInterval(this.notebookInterval)
    },
    beforeRouteLeave (to, from, next) {
      clearInterval(this.ideInterval)
      clearInterval(this.notebookInterval)
      next()
    },
    beforeDestroy () {
      clearInterval(this.ideInterval)
      clearInterval(this.notebookInterval)
    },
    deactivated () {
      clearInterval(this.ideInterval)
      clearInterval(this.notebookInterval)
    },
    methods: {
      parseJwt (token) {
        const base64Url = token.split('.')[1]
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
        const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(c => {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
        }).join(''))
        return JSON.parse(jsonPayload)
      },
      resetIdeInterval (skipLoad) {
        if (!skipLoad) {
          this.$store.dispatch('app/getIdeStatus')
        }
        if (this.ideInterval) {
          clearInterval(this.ideInterval)
        }
        this.ideInterval = setInterval(() => {
          this.$store.dispatch('app/getIdeStatus')
        }, 5000)
      },
      resetNotebookInterval (skipLoad) {
        if (!skipLoad) {
          this.$store.dispatch('app/getNotebookStatus')
        }
        if (this.ideInterval) {
          clearInterval(this.ideInterval)
        }
        this.ideInterval = setInterval(() => {
          this.$store.dispatch('app/getNotebookStatus')
        }, 5000)
      },
      async ideButtonClick () {
        switch (this.ideStatus) {
          case 'stopped':
            this.ideStatus = 'deploying'
            await this.$store.dispatch('app/startIde')
            this.resetIdeInterval(true)
            break
          case 'running':
            this.ideStatus = 'deploying'
            await this.$store.dispatch('app/stopIde')
            this.resetIdeInterval(true)
            break
        }
      },
      async notebookButtonClick () {
        switch (this.notebookStatus) {
          case 'stopped':
            this.notebookStatus = 'deploying'
            await this.$store.dispatch('app/startNotebook')
            this.resetNotebookInterval(true)
            break
          case 'running':
            this.notebookStatus = 'deploying'
            await this.$store.dispatch('app/stopNotebook')
            this.resetNodeInterval(true)
            break
        }
      },
    },
  }
</script>
<style scoped>
  .infinity-text {
    font-family: lato;
    font-weight: bold;
    letter-spacing: initial;
  }
</style>
